import { type ReactNode, useEffect, useReducer, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { CalendarDays, ChevronRight, Clock3, Home, MapPin, ShieldCheck, Star } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import Button from '../components/ui/Button'
import FormError from '../components/ui/FormError'
import Input from '../components/ui/Input'
import {
  CREATE_BOOKING_MUTATION,
  CREATE_PROPERTY_MUTATION,
  MY_PROPERTIES_QUERY,
  PROVIDER_FOR_BOOKING_QUERY,
  type BookingAvailableSlot,
  type BookingProperty,
  type BookingProviderProfile,
  type BookingServiceOffering,
  type CreateBookingData,
  type CreateBookingInput,
  type CreateBookingVariables,
  type CreatePropertyData,
  type CreatePropertyInput,
  type CreatePropertyVariables,
  type CreatedBooking,
  type MyPropertiesData,
  type ProviderForBookingData,
  type ProviderForBookingVariables,
} from '../graphql/booking'
import { useAuth } from '../contexts/AuthContext'
import { useLang, useScopedTranslations } from '../contexts/LangContext'
import { localized, normalizeLocale, type SupportedLocale } from '../lib/i18n'

type BookingStep = 1 | 2 | 3 | 'confirmation'
type PaymentMethod = 'card' | 'cash'

interface PropertyFormState {
  label: string
  propertyType: 'residential' | 'office' | 'airbnb' | 'other'
  addressLine1: string
  city: string
  postalCode: string
  hasElevator: boolean
  isDefault: boolean
}

interface BookingState {
  step: BookingStep
  serviceOfferingId: string
  scheduledDate: string
  startTime: string
  durationMinutes: number
  customerNotes: string
  propertyId: string
  paymentMethod: PaymentMethod
  addingProperty: boolean
  propertyForm: PropertyFormState
  createdBooking: CreatedBooking | null
}

type BookingAction =
  | { type: 'set_step'; step: BookingStep }
  | { type: 'initialize_service'; serviceOfferingId: string; durationMinutes: number }
  | { type: 'initialize_date'; scheduledDate: string }
  | { type: 'initialize_property'; propertyId: string }
  | { type: 'set_service'; serviceOfferingId: string; durationMinutes: number }
  | { type: 'set_date'; scheduledDate: string }
  | { type: 'set_start_time'; startTime: string }
  | { type: 'set_duration'; durationMinutes: number }
  | { type: 'set_customer_notes'; customerNotes: string }
  | { type: 'set_property'; propertyId: string }
  | { type: 'set_payment_method'; paymentMethod: PaymentMethod }
  | { type: 'toggle_add_property'; addingProperty?: boolean }
  | { type: 'update_property_form'; field: keyof PropertyFormState; value: string | boolean }
  | { type: 'property_created'; propertyId: string }
  | { type: 'booking_created'; booking: CreatedBooking }

const initialPropertyForm: PropertyFormState = {
  label: '',
  propertyType: 'residential',
  addressLine1: '',
  city: 'Athens',
  postalCode: '',
  hasElevator: false,
  isDefault: false,
}

const initialState: BookingState = {
  step: 1,
  serviceOfferingId: '',
  scheduledDate: '',
  startTime: '',
  durationMinutes: 0,
  customerNotes: '',
  propertyId: '',
  paymentMethod: 'card',
  addingProperty: false,
  propertyForm: initialPropertyForm,
  createdBooking: null,
}

const translations = {
  el: {
    eyebrow: 'Κράτηση',
    title: 'Ολοκλήρωσε την κράτησή σου βήμα-βήμα',
    body: 'Διάλεξε υπηρεσία, ώρα, ακίνητο και επιβεβαίωσε την κράτηση με πλήρη εικόνα πριν την αποστολή.',
    stepOne: 'Υπηρεσία & πρόγραμμα',
    stepTwo: 'Επιλογή ακινήτου',
    stepThree: 'Έλεγχος & επιβεβαίωση',
    stepDone: 'Έτοιμο',
    providerNotFoundTitle: 'Ο επαγγελματίας δεν βρέθηκε',
    providerNotFoundBody: 'Έλεγξε τον σύνδεσμο ή επίλεξε άλλον επαγγελματία από την αναζήτηση.',
    providerUnavailableTitle: 'Ο επαγγελματίας δεν δέχεται κρατήσεις αυτή τη στιγμή',
    providerUnavailableBody: 'Δοκίμασε άλλον επαγγελματία ή επίστρεψε στην αναζήτηση αργότερα.',
    providerVacationBody: 'Οι online κρατήσεις είναι προσωρινά κλειστές για αυτόν τον επαγγελματία.',
    providerScheduleUnavailableBody: 'Δεν υπάρχουν ανοιχτά διαθέσιμα ραντεβού για τις επόμενες 30 ημέρες.',
    providerAreaUnavailableBody: 'Δεν έχουν οριστεί ακόμη περιοχές εξυπηρέτησης για online κράτηση.',
    loadingTitle: 'Φορτώνουμε τα στοιχεία κράτησης...',
    serviceLabel: 'Υπηρεσία',
    dateLabel: 'Ημερομηνία',
    timeLabel: 'Ώρα έναρξης',
    durationLabel: 'Διάρκεια',
    notesLabel: 'Σημειώσεις για τον επαγγελματία',
    notesPlaceholder: 'Πληροφορίες πρόσβασης, ειδικές ανάγκες ή κάτι που πρέπει να γνωρίζει.',
    availableDates: 'Επόμενες διαθέσιμες ημερομηνίες',
    noAvailableDates: 'Δεν υπάρχουν ακόμη διαθέσιμες ημερομηνίες για τον επόμενο μήνα.',
    noTimesForDate: 'Δεν υπάρχουν διαθέσιμες ώρες για αυτή την ημερομηνία.',
    chooseDateHint: 'Επίλεξε ημερομηνία για να δεις διαθέσιμες ώρες.',
    next: 'Συνέχεια',
    back: 'Πίσω',
    createProperty: 'Προσθήκη νέου ακινήτου',
    cancelProperty: 'Άκυρο',
    saveProperty: 'Αποθήκευση ακινήτου',
    propertyLabel: 'Όνομα ακινήτου',
    propertyTypeLabel: 'Τύπος ακινήτου',
    addressLabel: 'Διεύθυνση',
    cityLabel: 'Πόλη',
    postalCodeLabel: 'Ταχυδρομικός κώδικας',
    elevatorLabel: 'Υπάρχει ασανσέρ',
    defaultLabel: 'Ορισμός ως προεπιλογή',
    matchingProperties: 'Ακίνητα που εξυπηρετούνται',
    unavailableProperties: 'Ακίνητα εκτός περιοχής επαγγελματία',
    noPropertiesTitle: 'Δεν έχεις αποθηκευμένα ακίνητα',
    noPropertiesBody: 'Πρόσθεσε ένα ακίνητο για να συνεχίσεις με την κράτηση.',
    unsupportedProperty: 'Ο επαγγελματίας δεν εξυπηρετεί αυτόν τον ταχυδρομικό κώδικα.',
    reviewTitle: 'Έλεγχος κράτησης',
    paymentMethod: 'Τρόπος πληρωμής',
    card: 'Κάρτα',
    cash: 'Μετρητά',
    confirmBooking: 'Επιβεβαίωση κράτησης',
    confirming: 'Αποστολή κράτησης...',
    bookingSuccessTitle: 'Η κράτηση στάλθηκε',
    bookingSuccessBody: 'Ο επαγγελματίας θα δει το αίτημά σου και θα απαντήσει σύντομα.',
    bookingId: 'Κωδικός κράτησης',
    priceEstimate: 'Εκτιμώμενη χρέωση',
    rateApplied: 'Τιμή υπηρεσίας',
    selectedProperty: 'Ακίνητο',
    propertyRequired: 'Επίλεξε ακίνητο για να συνεχίσεις.',
    propertyAreaMismatch: 'Το ακίνητο πρέπει να βρίσκεται σε περιοχή που εξυπηρετεί ο επαγγελματίας.',
    propertyValidation: 'Συμπλήρωσε όνομα ακινήτου, διεύθυνση, πόλη και ταχυδρομικό κώδικα.',
    propertyCreateFailed: 'Δεν ήταν δυνατή η αποθήκευση του ακινήτου αυτή τη στιγμή.',
    bookingSummary: 'Σύνοψη',
    serviceSummary: 'Υπηρεσία',
    scheduleSummary: 'Ραντεβού',
    durationSummary: 'Διάρκεια',
    notesSummary: 'Σημειώσεις',
    paymentSummary: 'Πληρωμή',
    noNotes: 'Χωρίς σημειώσεις',
    bookingValidation: 'Συμπλήρωσε υπηρεσία, ώρα και ακίνητο για να συνεχίσεις.',
    bookingCreateFailed: 'Δεν ήταν δυνατή η αποστολή της κράτησης αυτή τη στιγμή.',
    goToBookings: 'Δες τις κρατήσεις μου',
    searchAgain: 'Επιστροφή στην αναζήτηση',
    propertyResidential: 'Κατοικία',
    propertyOffice: 'Γραφείο',
    propertyAirbnb: 'Airbnb',
    propertyOther: 'Άλλο',
    durationMinutes: 'λεπτά',
    experienceYear: 'χρόνο εμπειρίας',
    experienceYears: 'χρόνια εμπειρίας',
    experienceUnavailable: 'Εμπειρία μη διαθέσιμη',
    rateUnavailable: 'Τιμή μη διαθέσιμη',
    verified: 'Επαληθευμένος',
    today: 'Σήμερα',
    availableAgain: 'Διαθέσιμος ξανά',
  },
  en: {
    eyebrow: 'Booking',
    title: 'Complete your booking step by step',
    body: 'Choose the service, time, property, and payment preference with full context before you submit.',
    stepOne: 'Service & schedule',
    stepTwo: 'Property selection',
    stepThree: 'Review & confirm',
    stepDone: 'Done',
    providerNotFoundTitle: 'Provider not found',
    providerNotFoundBody: 'Check the link or choose another provider from search.',
    providerUnavailableTitle: 'This provider is not accepting bookings right now',
    providerUnavailableBody: 'Try another provider or return to search later.',
    providerVacationBody: 'Online booking is temporarily paused for this provider.',
    providerScheduleUnavailableBody: 'No bookable time slots are open in the next 30 days.',
    providerAreaUnavailableBody: 'No service areas have been published for online booking yet.',
    loadingTitle: 'Loading booking details...',
    serviceLabel: 'Service',
    dateLabel: 'Date',
    timeLabel: 'Start time',
    durationLabel: 'Duration',
    notesLabel: 'Notes for the provider',
    notesPlaceholder: 'Access instructions, special requests, or anything they should know.',
    availableDates: 'Next available dates',
    noAvailableDates: 'No available dates are open in the next month yet.',
    noTimesForDate: 'No times are available for this date.',
    chooseDateHint: 'Pick a date to see available times.',
    next: 'Next',
    back: 'Back',
    createProperty: 'Add new property',
    cancelProperty: 'Cancel',
    saveProperty: 'Save property',
    propertyLabel: 'Property label',
    propertyTypeLabel: 'Property type',
    addressLabel: 'Address',
    cityLabel: 'City',
    postalCodeLabel: 'Postal code',
    elevatorLabel: 'Elevator available',
    defaultLabel: 'Set as default',
    matchingProperties: 'Supported properties',
    unavailableProperties: 'Properties outside this provider area',
    noPropertiesTitle: 'No saved properties yet',
    noPropertiesBody: 'Add a property to continue with the booking.',
    unsupportedProperty: 'This provider does not serve that postal code.',
    reviewTitle: 'Review your booking',
    paymentMethod: 'Payment method',
    card: 'Card',
    cash: 'Cash',
    confirmBooking: 'Confirm booking',
    confirming: 'Submitting booking...',
    bookingSuccessTitle: 'Booking request sent',
    bookingSuccessBody: 'The provider will review your request and respond soon.',
    bookingId: 'Booking ID',
    priceEstimate: 'Estimated charge',
    rateApplied: 'Applied service rate',
    selectedProperty: 'Property',
    propertyRequired: 'Choose a property to continue.',
    propertyAreaMismatch: 'The property must be in an area served by this provider.',
    propertyValidation: 'Fill in the property label, address, city, and postal code.',
    propertyCreateFailed: 'We could not save this property right now.',
    bookingSummary: 'Summary',
    serviceSummary: 'Service',
    scheduleSummary: 'Schedule',
    durationSummary: 'Duration',
    notesSummary: 'Notes',
    paymentSummary: 'Payment',
    noNotes: 'No notes',
    bookingValidation: 'Choose a service, time, and property to continue.',
    bookingCreateFailed: 'We could not submit the booking right now.',
    goToBookings: 'View my bookings',
    searchAgain: 'Back to search',
    propertyResidential: 'Residential',
    propertyOffice: 'Office',
    propertyAirbnb: 'Airbnb',
    propertyOther: 'Other',
    durationMinutes: 'minutes',
    experienceYear: 'year experience',
    experienceYears: 'years experience',
    experienceUnavailable: 'Experience unavailable',
    rateUnavailable: 'Rate unavailable',
    verified: 'Verified',
    today: 'Today',
    availableAgain: 'Available again',
  },
} as const

type BookingCopy = Record<keyof typeof translations.en, string>

const PROPERTY_TYPE_OPTIONS = [
  { value: 'residential', el: translations.el.propertyResidential, en: translations.en.propertyResidential },
  { value: 'office', el: translations.el.propertyOffice, en: translations.en.propertyOffice },
  { value: 'airbnb', el: translations.el.propertyAirbnb, en: translations.en.propertyAirbnb },
  { value: 'other', el: translations.el.propertyOther, en: translations.en.propertyOther },
] as const

function bookingReducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case 'set_step':
      return { ...state, step: action.step }
    case 'initialize_service':
      if (state.serviceOfferingId) return state
      return {
        ...state,
        serviceOfferingId: action.serviceOfferingId,
        durationMinutes: action.durationMinutes,
      }
    case 'initialize_date':
      if (state.scheduledDate) return state
      return { ...state, scheduledDate: action.scheduledDate }
    case 'initialize_property':
      if (state.propertyId) return state
      return { ...state, propertyId: action.propertyId }
    case 'set_service':
      return {
        ...state,
        serviceOfferingId: action.serviceOfferingId,
        durationMinutes: action.durationMinutes,
        startTime: '',
      }
    case 'set_date':
      return {
        ...state,
        scheduledDate: action.scheduledDate,
        startTime: '',
      }
    case 'set_start_time':
      return { ...state, startTime: action.startTime }
    case 'set_duration':
      return { ...state, durationMinutes: action.durationMinutes, startTime: '' }
    case 'set_customer_notes':
      return { ...state, customerNotes: action.customerNotes }
    case 'set_property':
      return { ...state, propertyId: action.propertyId }
    case 'set_payment_method':
      return { ...state, paymentMethod: action.paymentMethod }
    case 'toggle_add_property':
      return { ...state, addingProperty: action.addingProperty ?? !state.addingProperty }
    case 'update_property_form':
      return {
        ...state,
        propertyForm: {
          ...state.propertyForm,
          [action.field]: action.value,
        },
      }
    case 'property_created':
      return {
        ...state,
        propertyId: action.propertyId,
        addingProperty: false,
        propertyForm: initialPropertyForm,
      }
    case 'booking_created':
      return {
        ...state,
        createdBooking: action.booking,
        step: 'confirmation',
      }
    default:
      return state
  }
}

function todayIso() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDaysIso(startDate: string, days: number) {
  const baseDate = new Date(`${startDate}T12:00:00`)
  baseDate.setDate(baseDate.getDate() + days)
  const year = baseDate.getFullYear()
  const month = String(baseDate.getMonth() + 1).padStart(2, '0')
  const day = String(baseDate.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function normalizeTime(timeValue: string) {
  return timeValue.slice(0, 5)
}

function parseTimeToMinutes(timeValue: string) {
  const [hours, minutes] = normalizeTime(timeValue).split(':').map(Number)
  return hours * 60 + minutes
}

function formatMinutes(minutes: number) {
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}`
}

function buildTimeOptions(slots: BookingAvailableSlot[], durationMinutes: number) {
  const options = new Set<string>()

  slots.forEach((slot) => {
    const startMinutes = parseTimeToMinutes(slot.startTime)
    const endMinutes = parseTimeToMinutes(slot.endTime)

    for (let currentMinutes = startMinutes; currentMinutes + durationMinutes <= endMinutes; currentMinutes += 30) {
      options.add(formatMinutes(currentMinutes))
    }
  })

  return Array.from(options).sort()
}

function formatDate(dateValue: string, locale: SupportedLocale) {
  return new Intl.DateTimeFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${dateValue}T12:00:00`))
}

function formatLongDate(dateValue: string, locale: SupportedLocale) {
  return new Intl.DateTimeFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(`${dateValue}T12:00:00`))
}

function providerUnavailableMessage(
  provider: BookingProviderProfile,
  availableDates: string[],
  locale: SupportedLocale,
  copy: BookingCopy,
) {
  if (provider.onVacation) {
    if (!provider.vacationUntil) return copy.providerVacationBody
    return `${copy.providerVacationBody} ${copy.availableAgain}: ${formatLongDate(provider.vacationUntil, locale)}.`
  }

  if (provider.serviceAreas.length === 0) return copy.providerAreaUnavailableBody
  if (availableDates.length === 0) return copy.providerScheduleUnavailableBody

  return copy.providerUnavailableBody
}

function formatCurrency(cents: number | null, locale: SupportedLocale, copy: BookingCopy) {
  if (cents === null) return copy.rateUnavailable

  return new Intl.NumberFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

function providerDisplayName(provider: BookingProviderProfile) {
  const lastInitial = provider.user.lastName.charAt(0)
  return lastInitial ? `${provider.user.firstName} ${lastInitial}.` : provider.user.firstName
}

function experienceLabel(yearsExperience: number | null, copy: BookingCopy) {
  if (yearsExperience === null) return copy.experienceUnavailable
  return `${yearsExperience} ${yearsExperience === 1 ? copy.experienceYear : copy.experienceYears}`
}

function offeringBaseDuration(offering: BookingServiceOffering) {
  return offering.customDurationMinutes ?? offering.serviceType.estimatedDurationMinutes
}

function offeringRateCents(offering: BookingServiceOffering) {
  return offering.customPriceCents ?? offering.serviceType.basePriceCents
}

function priceEstimateCents(offering: BookingServiceOffering, durationMinutes: number) {
  return Math.round((offeringRateCents(offering) / 60) * durationMinutes)
}

function StepCard({ children, title }: { children: ReactNode; title: string }) {
  return (
    <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-2xl font-bold text-slate-950">{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  )
}

function StepPills({
  currentStep,
  copy,
}: {
  currentStep: BookingStep
  copy: BookingCopy
}) {
  const steps = [
    { id: 1, label: copy.stepOne },
    { id: 2, label: copy.stepTwo },
    { id: 3, label: copy.stepThree },
  ] as const

  return (
    <div className="flex flex-wrap gap-3">
      {steps.map((step) => {
        const isActive = currentStep === step.id
        const isComplete = currentStep === 'confirmation' || (typeof currentStep === 'number' && currentStep > step.id)

        return (
          <div
            key={step.id}
            className={[
              'inline-flex items-center gap-3 rounded-full px-4 py-2 text-sm font-semibold',
              isActive
                ? 'bg-primary-600 text-white'
                : isComplete
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-white text-slate-500 border border-slate-200',
            ].join(' ')}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-xs font-bold">
              {step.id}
            </span>
            <span>{step.label}</span>
          </div>
        )
      })}
    </div>
  )
}

function PropertyCard({
  locale,
  property,
  selectable,
  selected,
  onSelect,
}: {
  locale: SupportedLocale
  property: BookingProperty
  selectable: boolean
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={!selectable}
      className={[
        'w-full rounded-2xl border p-4 text-left transition',
        selectable
          ? selected
            ? 'border-primary-500 bg-primary-50 shadow-sm'
            : 'border-slate-200 bg-white hover:border-primary-200 hover:bg-slate-50'
          : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-slate-950">{property.label}</p>
          <p className="mt-1 text-sm text-slate-600">{property.addressLine1}</p>
          <p className="mt-1 text-sm text-slate-500">{property.city || 'Athens'}, {property.postalCode}</p>
        </div>
        {property.isDefault && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            {locale === 'el' ? 'Προεπιλογή' : 'Default'}
          </span>
        )}
      </div>
    </button>
  )
}

export default function BookingPage() {
  const { providerProfileId } = useParams()
  const { currentUser } = useAuth()
  const { locale: appLocale } = useLang()
  const activeLocale = normalizeLocale(currentUser?.locale ?? appLocale)
  const copy = useScopedTranslations('booking.create', translations[activeLocale], activeLocale)

  const [state, dispatch] = useReducer(bookingReducer, initialState)
  const [propertyErrors, setPropertyErrors] = useState<string[]>([])
  const [bookingErrors, setBookingErrors] = useState<string[]>([])

  const availabilityFrom = todayIso()
  const availabilityTo = addDaysIso(availabilityFrom, 30)

  const { data: providerData, loading: providerLoading, error: providerError } = useQuery<ProviderForBookingData, ProviderForBookingVariables>(
    PROVIDER_FOR_BOOKING_QUERY,
    {
      variables: {
        id: providerProfileId ?? '',
        availabilityFrom,
        availabilityTo,
      },
      skip: !providerProfileId,
    },
  )

  const {
    data: propertiesData,
    loading: propertiesLoading,
    refetch: refetchProperties,
  } = useQuery<MyPropertiesData>(MY_PROPERTIES_QUERY)

  const [createProperty, { loading: creatingProperty }] = useMutation<CreatePropertyData, CreatePropertyVariables>(CREATE_PROPERTY_MUTATION)
  const [createBooking, { loading: creatingBooking }] = useMutation<CreateBookingData, CreateBookingVariables>(CREATE_BOOKING_MUTATION)

  const provider = providerData?.providerProfile
  const properties = propertiesData?.myProperties ?? []
  const serviceAreaPostalCodes = new Set(provider?.serviceAreas.map((area) => area.postalCode) ?? [])
  const matchingProperties = properties.filter((property) => serviceAreaPostalCodes.has(property.postalCode))
  const unavailableProperties = properties.filter((property) => !serviceAreaPostalCodes.has(property.postalCode))
  const availableDates = Array.from(new Set(provider?.availableSlots.map((slot) => slot.date) ?? [])).sort()
  const selectedOffering = provider?.serviceOfferings.find((offering) => offering.id === state.serviceOfferingId) ?? provider?.serviceOfferings[0] ?? null
  const slotsForSelectedDate = provider?.availableSlots.filter((slot) => slot.date === state.scheduledDate) ?? []
  const timeOptions = state.durationMinutes > 0 ? buildTimeOptions(slotsForSelectedDate, state.durationMinutes) : []
  const selectedProperty = properties.find((property) => property.id === state.propertyId) ?? null
  const stepOneReady = Boolean(selectedOffering && state.scheduledDate && state.startTime)
  const stepTwoReady = Boolean(selectedProperty)
  const durationOptions = selectedOffering
    ? Array.from({ length: 16 }, (_, index) => 30 * (index + 1)).filter((minutes) => minutes >= offeringBaseDuration(selectedOffering))
    : []
  const providerUnavailable = Boolean(
    provider && (
      provider.onVacation ||
      provider.serviceOfferings.length === 0 ||
      provider.serviceAreas.length === 0 ||
      availableDates.length === 0
    ),
  )

  useEffect(() => {
    if (!provider || provider.serviceOfferings.length === 0) return

    dispatch({
      type: 'initialize_service',
      serviceOfferingId: provider.serviceOfferings[0].id,
      durationMinutes: offeringBaseDuration(provider.serviceOfferings[0]),
    })
  }, [provider])

  useEffect(() => {
    if (!availableDates.length) return
    dispatch({ type: 'initialize_date', scheduledDate: availableDates[0] })
  }, [availableDates])

  useEffect(() => {
    if (!matchingProperties.length) return
    dispatch({ type: 'initialize_property', propertyId: matchingProperties[0].id })
  }, [matchingProperties])

  useEffect(() => {
    if (!state.startTime) return
    if (timeOptions.includes(state.startTime)) return
    dispatch({ type: 'set_start_time', startTime: '' })
  }, [state.startTime, timeOptions])

  async function handleCreateProperty() {
    const input: CreatePropertyInput = {
      label: state.propertyForm.label.trim(),
      propertyType: state.propertyForm.propertyType,
      addressLine1: state.propertyForm.addressLine1.trim(),
      city: state.propertyForm.city.trim(),
      postalCode: state.propertyForm.postalCode.trim(),
      country: 'GR',
      hasElevator: state.propertyForm.hasElevator,
      isDefault: state.propertyForm.isDefault || properties.length === 0,
    }

    if (!input.label || !input.addressLine1 || !input.city || !input.postalCode) {
      setPropertyErrors([copy.propertyValidation])
      return
    }

    if (!serviceAreaPostalCodes.has(input.postalCode)) {
      setPropertyErrors([copy.propertyAreaMismatch])
      return
    }

    try {
      const { data } = await createProperty({ variables: { input } })
      const result = data?.createProperty

      if (!result) {
        setPropertyErrors([copy.propertyCreateFailed])
        return
      }

      if (result.errors.length > 0 || !result.property) {
        setPropertyErrors(result.errors)
        return
      }

      setPropertyErrors([])
      dispatch({ type: 'property_created', propertyId: result.property.id })
      await refetchProperties()
    } catch {
      setPropertyErrors([copy.propertyCreateFailed])
    }
  }

  async function handleConfirmBooking() {
    if (!providerProfileId || !selectedOffering || !selectedProperty || !state.scheduledDate || !state.startTime) {
      setBookingErrors([copy.bookingValidation])
      return
    }

    const input: CreateBookingInput = {
      providerProfileId,
      serviceTypeId: selectedOffering.serviceType.id,
      propertyId: selectedProperty.id,
      scheduledDate: state.scheduledDate,
      startTime: state.startTime,
      durationMinutes: state.durationMinutes,
      customerNotes: state.customerNotes.trim() || undefined,
      paymentMethod: state.paymentMethod,
    }

    try {
      const { data } = await createBooking({ variables: { input } })
      const result = data?.createBooking

      if (!result) {
        setBookingErrors([copy.bookingCreateFailed])
        return
      }

      if (result.errors.length > 0 || !result.booking) {
        setBookingErrors(result.errors)
        return
      }

      setBookingErrors([])
      dispatch({ type: 'booking_created', booking: result.booking })
    } catch {
      setBookingErrors([copy.bookingCreateFailed])
    }
  }

  if (providerLoading || propertiesLoading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6">
        <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
          <p className="text-lg font-semibold text-slate-600">{copy.loadingTitle}</p>
        </div>
      </div>
    )
  }

  if (providerError || !provider) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
        <StepCard title={copy.providerNotFoundTitle}>
          <p className="text-sm leading-7 text-slate-600">{copy.providerNotFoundBody}</p>
          <div className="mt-6">
            <Link to="/search" className="text-sm font-semibold text-primary-600 hover:underline">
              {copy.searchAgain}
            </Link>
          </div>
        </StepCard>
      </div>
    )
  }

  if (providerUnavailable) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
        <StepCard title={copy.providerUnavailableTitle}>
          <p className="text-sm leading-7 text-slate-600">{providerUnavailableMessage(provider, availableDates, activeLocale, copy)}</p>
          <div className="mt-6">
            <Link to="/search" className="text-sm font-semibold text-primary-600 hover:underline">
              {copy.searchAgain}
            </Link>
          </div>
        </StepCard>
      </div>
    )
  }

  if (state.step === 'confirmation' && state.createdBooking) {
    const createdBooking = state.createdBooking
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
        <StepPills currentStep="confirmation" copy={copy} />
        <div className="mt-8 rounded-[2rem] border border-emerald-100 bg-emerald-50 p-8 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-700">{copy.stepDone}</p>
          <h1 className="mt-3 text-3xl font-bold text-emerald-950">{copy.bookingSuccessTitle}</h1>
          <p className="mt-4 text-base leading-8 text-emerald-900">{copy.bookingSuccessBody}</p>

          <div className="mt-8 grid gap-4 rounded-[1.5rem] bg-white p-6 shadow-sm sm:grid-cols-2">
            <div>
              <p className="text-sm font-semibold text-slate-500">{copy.bookingId}</p>
              <p className="mt-1 text-lg font-bold text-slate-950">#{createdBooking.id}</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-500">{copy.scheduleSummary}</p>
              <p className="mt-1 text-lg font-bold text-slate-950">
                {formatLongDate(createdBooking.scheduledDate, activeLocale)} · {normalizeTime(createdBooking.startTime)}
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-500">{copy.serviceSummary}</p>
              <p className="mt-1 text-lg font-bold text-slate-950">
                {localized(createdBooking.serviceType, activeLocale)}
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-500">{copy.priceEstimate}</p>
              <p className="mt-1 text-lg font-bold text-primary-700">
                {formatCurrency(createdBooking.priceCents, activeLocale, copy)}
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/bookings/${createdBooking.id}`}
              className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              {copy.goToBookings}
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-white px-5 py-3 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100"
            >
              {copy.searchAgain}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">{copy.eyebrow}</p>
      <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">{copy.title}</h1>
      <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">{copy.body}</p>

      <div className="mt-8">
        <StepPills currentStep={state.step} copy={copy} />
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          {state.step === 1 && (
            <StepCard title={copy.stepOne}>
              <div className="rounded-[1.5rem] bg-slate-950 p-6 text-white">
                <div className="flex items-start gap-4">
                  {provider.user.avatarUrl ? (
                    <img src={provider.user.avatarUrl} alt="" className="h-16 w-16 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/15 text-lg font-bold">
                      {provider.user.firstName.charAt(0)}
                      {provider.user.lastName.charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-2xl font-bold">{providerDisplayName(provider)}</h2>
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-amber-200">
                        <Star className="h-4 w-4 fill-current" />
                        {provider.averageRating.toFixed(1)} ({provider.totalReviews})
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-slate-300">{experienceLabel(provider.yearsExperience, copy)}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      {provider.verified && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-200">
                          <ShieldCheck className="h-4 w-4" />
                          {copy.verified}
                        </span>
                      )}
                      <span className="text-sm font-semibold text-slate-200">
                        {formatCurrency(provider.hourlyRateCents, activeLocale, copy)}
                        {provider.hourlyRateCents !== null && `/${activeLocale === 'el' ? 'ώρα' : 'hr'}`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="booking-service" className="mb-2 block text-sm font-semibold text-slate-700">
                    {copy.serviceLabel}
                  </label>
                  <select
                    id="booking-service"
                    value={state.serviceOfferingId}
                    onChange={(event) => {
                      const nextOffering = provider.serviceOfferings.find((offering) => offering.id === event.target.value)
                      if (!nextOffering) return
                      dispatch({
                        type: 'set_service',
                        serviceOfferingId: nextOffering.id,
                        durationMinutes: offeringBaseDuration(nextOffering),
                      })
                    }}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
                  >
                    {provider.serviceOfferings.map((offering) => (
                      <option key={offering.id} value={offering.id}>
                        {localized(offering.serviceType, activeLocale)} · {localized(offering.serviceType.serviceCategory, activeLocale)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="booking-duration" className="mb-2 block text-sm font-semibold text-slate-700">
                    {copy.durationLabel}
                  </label>
                  <select
                    id="booking-duration"
                    value={state.durationMinutes}
                    onChange={(event) => dispatch({ type: 'set_duration', durationMinutes: Number(event.target.value) })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
                  >
                    {durationOptions.map((minutes) => (
                      <option key={minutes} value={minutes}>
                        {minutes} {copy.durationMinutes}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <p className="text-sm font-semibold text-slate-700">{copy.availableDates}</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {availableDates.length === 0 && (
                    <p className="rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-500">{copy.noAvailableDates}</p>
                  )}
                  {availableDates.map((date) => (
                    <button
                      key={date}
                      type="button"
                      onClick={() => dispatch({ type: 'set_date', scheduledDate: date })}
                      className={[
                        'rounded-xl border px-4 py-3 text-sm font-semibold transition',
                        state.scheduledDate === date
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-primary-200 hover:text-primary-700',
                      ].join(' ')}
                    >
                      {date === availabilityFrom ? copy.today : formatDate(date, activeLocale)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="booking-date" className="mb-2 block text-sm font-semibold text-slate-700">
                    {copy.dateLabel}
                  </label>
                  <div className="relative">
                    <input
                      id="booking-date"
                      type="date"
                      value={state.scheduledDate}
                      min={availabilityFrom}
                      max={availabilityTo}
                      onChange={(event) => dispatch({ type: 'set_date', scheduledDate: event.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
                    />
                    <CalendarDays className="pointer-events-none absolute right-4 top-3.5 h-4 w-4 text-slate-400" />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">{copy.timeLabel}</label>
                  {state.scheduledDate === '' && (
                    <p className="rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-500">{copy.chooseDateHint}</p>
                  )}
                  {state.scheduledDate !== '' && timeOptions.length === 0 && (
                    <p className="rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-500">{copy.noTimesForDate}</p>
                  )}
                  {timeOptions.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {timeOptions.map((timeOption) => (
                        <button
                          key={timeOption}
                          type="button"
                          onClick={() => dispatch({ type: 'set_start_time', startTime: timeOption })}
                          className={[
                            'rounded-xl border px-3 py-3 text-sm font-semibold transition',
                            state.startTime === timeOption
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-primary-200 hover:text-primary-700',
                          ].join(' ')}
                        >
                          {timeOption}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="booking-notes" className="mb-2 block text-sm font-semibold text-slate-700">
                  {copy.notesLabel}
                </label>
                <textarea
                  id="booking-notes"
                  value={state.customerNotes}
                  onChange={(event) => dispatch({ type: 'set_customer_notes', customerNotes: event.target.value })}
                  rows={4}
                  placeholder={copy.notesPlaceholder}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
                />
              </div>

              <div className="mt-8 flex flex-wrap justify-end gap-3">
                <Button type="button" size="lg" onClick={() => dispatch({ type: 'set_step', step: 2 })} disabled={!stepOneReady}>
                  {copy.next}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </StepCard>
          )}

          {state.step === 2 && (
            <StepCard title={copy.stepTwo}>
              <FormError errors={propertyErrors} />

              {matchingProperties.length === 0 && !state.addingProperty && (
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-6">
                  <h3 className="text-lg font-bold text-slate-950">{copy.noPropertiesTitle}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{copy.noPropertiesBody}</p>
                </div>
              )}

              {matchingProperties.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">{copy.matchingProperties}</h3>
                  <div className="mt-4 grid gap-3">
                    {matchingProperties.map((property) => (
                      <PropertyCard
                        key={property.id}
                        locale={activeLocale}
                        property={property}
                        selectable
                        selected={state.propertyId === property.id}
                        onSelect={() => dispatch({ type: 'set_property', propertyId: property.id })}
                      />
                    ))}
                  </div>
                </div>
              )}

              {unavailableProperties.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">{copy.unavailableProperties}</h3>
                  <div className="mt-4 grid gap-3">
                    {unavailableProperties.map((property) => (
                      <PropertyCard
                        key={property.id}
                        locale={activeLocale}
                        property={property}
                        selectable={false}
                        selected={false}
                        onSelect={() => undefined}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant={state.addingProperty ? 'secondary' : 'primary'}
                  onClick={() => {
                    setPropertyErrors([])
                    dispatch({ type: 'toggle_add_property' })
                  }}
                >
                  {state.addingProperty ? copy.cancelProperty : copy.createProperty}
                </Button>
              </div>

              {state.addingProperty && (
                <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label={copy.propertyLabel}
                      value={state.propertyForm.label}
                      onChange={(event) => dispatch({ type: 'update_property_form', field: 'label', value: event.target.value })}
                      required
                    />
                    <div>
                      <label htmlFor="property-type" className="mb-2 block text-sm font-semibold text-slate-700">
                        {copy.propertyTypeLabel}
                      </label>
                      <select
                        id="property-type"
                        value={state.propertyForm.propertyType}
                        onChange={(event) => dispatch({ type: 'update_property_form', field: 'propertyType', value: event.target.value })}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
                      >
                        {PROPERTY_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option[activeLocale]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Input
                      label={copy.addressLabel}
                      value={state.propertyForm.addressLine1}
                      onChange={(event) => dispatch({ type: 'update_property_form', field: 'addressLine1', value: event.target.value })}
                      required
                    />
                    <Input
                      label={copy.cityLabel}
                      value={state.propertyForm.city}
                      onChange={(event) => dispatch({ type: 'update_property_form', field: 'city', value: event.target.value })}
                      required
                    />
                    <Input
                      label={copy.postalCodeLabel}
                      inputMode="numeric"
                      value={state.propertyForm.postalCode}
                      onChange={(event) => dispatch({
                        type: 'update_property_form',
                        field: 'postalCode',
                        value: event.target.value.replace(/\D/g, '').slice(0, 5),
                      })}
                      required
                    />
                    <div className="flex flex-col justify-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
                      <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
                        <input
                          type="checkbox"
                          checked={state.propertyForm.hasElevator}
                          onChange={(event) => dispatch({ type: 'update_property_form', field: 'hasElevator', value: event.target.checked })}
                        />
                        {copy.elevatorLabel}
                      </label>
                      <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
                        <input
                          type="checkbox"
                          checked={state.propertyForm.isDefault}
                          onChange={(event) => dispatch({ type: 'update_property_form', field: 'isDefault', value: event.target.checked })}
                        />
                        {copy.defaultLabel}
                      </label>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap justify-end gap-3">
                    <Button type="button" variant="secondary" onClick={() => dispatch({ type: 'toggle_add_property', addingProperty: false })}>
                      {copy.cancelProperty}
                    </Button>
                    <Button type="button" loading={creatingProperty} onClick={handleCreateProperty}>
                      {copy.saveProperty}
                    </Button>
                  </div>
                </div>
              )}

              <div className="mt-8 flex flex-wrap justify-between gap-3">
                <Button type="button" variant="secondary" size="lg" onClick={() => dispatch({ type: 'set_step', step: 1 })}>
                  {copy.back}
                </Button>
                <Button type="button" size="lg" onClick={() => dispatch({ type: 'set_step', step: 3 })} disabled={!stepTwoReady}>
                  {copy.next}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </StepCard>
          )}

          {state.step === 3 && selectedOffering && selectedProperty && (
            <StepCard title={copy.reviewTitle}>
              <FormError errors={bookingErrors} />

              <div className="grid gap-6 md:grid-cols-2">
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <h3 className="text-lg font-bold text-slate-950">{copy.bookingSummary}</h3>
                  <dl className="mt-5 space-y-4 text-sm text-slate-600">
                    <div>
                      <dt className="font-semibold text-slate-900">{copy.serviceSummary}</dt>
                      <dd className="mt-1">{localized(selectedOffering.serviceType, activeLocale)}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-slate-900">{copy.scheduleSummary}</dt>
                      <dd className="mt-1">
                        {formatLongDate(state.scheduledDate, activeLocale)} · {state.startTime}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-slate-900">{copy.durationSummary}</dt>
                      <dd className="mt-1">{state.durationMinutes} {copy.durationMinutes}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-slate-900">{copy.selectedProperty}</dt>
                      <dd className="mt-1">
                        {selectedProperty.label} · {selectedProperty.addressLine1}, {selectedProperty.city || 'Athens'} {selectedProperty.postalCode}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-slate-900">{copy.notesSummary}</dt>
                      <dd className="mt-1 whitespace-pre-wrap">{state.customerNotes.trim() || copy.noNotes}</dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="text-lg font-bold text-slate-950">{copy.priceEstimate}</h3>
                  <div className="mt-5 space-y-4 text-sm text-slate-600">
                    <div className="flex items-center justify-between gap-3">
                      <span>{copy.rateApplied}</span>
                      <span className="font-semibold text-slate-900">
                        {formatCurrency(offeringRateCents(selectedOffering), activeLocale, copy)}
                        /{activeLocale === 'el' ? 'ώρα' : 'hr'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span>{copy.durationSummary}</span>
                      <span className="font-semibold text-slate-900">{state.durationMinutes} {copy.durationMinutes}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
                      <span className="font-semibold text-slate-900">{copy.priceEstimate}</span>
                      <span className="text-xl font-bold text-primary-700">
                        {formatCurrency(priceEstimateCents(selectedOffering, state.durationMinutes), activeLocale, copy)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6">
                    <p className="text-sm font-semibold text-slate-700">{copy.paymentMethod}</p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {(['card', 'cash'] as const).map((paymentMethod) => (
                        <button
                          key={paymentMethod}
                          type="button"
                          onClick={() => dispatch({ type: 'set_payment_method', paymentMethod })}
                          className={[
                            'rounded-xl border px-4 py-3 text-left text-sm font-semibold transition',
                            state.paymentMethod === paymentMethod
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-primary-200 hover:text-primary-700',
                          ].join(' ')}
                        >
                          {paymentMethod === 'card' ? copy.card : copy.cash}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap justify-between gap-3">
                <Button type="button" variant="secondary" size="lg" onClick={() => dispatch({ type: 'set_step', step: 2 })}>
                  {copy.back}
                </Button>
                <Button type="button" size="lg" loading={creatingBooking} onClick={handleConfirmBooking}>
                  {copy.confirmBooking}
                </Button>
              </div>
            </StepCard>
          )}
        </div>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <StepCard title={copy.bookingSummary}>
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                {provider.user.avatarUrl ? (
                  <img src={provider.user.avatarUrl} alt="" className="h-12 w-12 rounded-full object-cover" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                    {provider.user.firstName.charAt(0)}
                    {provider.user.lastName.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-slate-500">{copy.eyebrow}</p>
                  <p className="text-lg font-bold text-slate-950">{providerDisplayName(provider)}</p>
                </div>
              </div>

              <div className="grid gap-3 text-sm text-slate-600">
                <div className="flex items-start gap-3">
                  <Home className="mt-0.5 h-4 w-4 text-slate-400" />
                  <div>
                    <p className="font-semibold text-slate-900">{copy.serviceSummary}</p>
                    <p className="mt-1">
                      {selectedOffering ? localized(selectedOffering.serviceType, activeLocale) : '—'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CalendarDays className="mt-0.5 h-4 w-4 text-slate-400" />
                  <div>
                    <p className="font-semibold text-slate-900">{copy.scheduleSummary}</p>
                    <p className="mt-1">
                      {state.scheduledDate ? formatDate(state.scheduledDate, activeLocale) : '—'}
                      {state.startTime ? ` · ${state.startTime}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock3 className="mt-0.5 h-4 w-4 text-slate-400" />
                  <div>
                    <p className="font-semibold text-slate-900">{copy.durationSummary}</p>
                    <p className="mt-1">{state.durationMinutes ? `${state.durationMinutes} ${copy.durationMinutes}` : '—'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 text-slate-400" />
                  <div>
                    <p className="font-semibold text-slate-900">{copy.selectedProperty}</p>
                    <p className="mt-1">
                      {selectedProperty ? `${selectedProperty.label} · ${selectedProperty.postalCode}` : '—'}
                    </p>
                  </div>
                </div>
              </div>

              {selectedOffering && (
                <div className="rounded-2xl bg-primary-50 px-4 py-4">
                  <p className="text-sm font-semibold text-primary-700">{copy.priceEstimate}</p>
                  <p className="mt-2 text-2xl font-black text-primary-900">
                    {formatCurrency(priceEstimateCents(selectedOffering, state.durationMinutes || offeringBaseDuration(selectedOffering)), activeLocale, copy)}
                  </p>
                </div>
              )}
            </div>
          </StepCard>
        </aside>
      </div>
    </div>
  )
}
